import { observable, action, IObservableArray, toJS, computed } from "mobx";
import type { Callback } from "./_internal";

// --

// IObservableListOpts
export interface IObservableListOpts {
	deep?: boolean;
}

/**
 * Observable store
 */
export class ObservableList<I extends {}> {
	// On before change
	public onBeforeChange?(data: I[]): void;

	// Default data
	public defaults: I[];

	// Data storage
	public readonly data: IObservableArray<I>;

	/**
	 * Class constructor
	 * @param data
	 * @param opts
	 */
	public constructor(data: I[], protected readonly opts?: IObservableListOpts) {
		opts || (opts = {});
		typeof opts.deep === "boolean" || (opts.deep = false);
		this.data = observable.array(data, { deep: opts.deep });
		if (!this.defaults) this.defaults = data;
	}

	/**
	 * Length
	 */
	public get length() {
		return this.data.length;
	}

	/**
	 * Item
	 * @param index
	 * @returns
	 */
	public item(index: number) {
		return this.data[index];
	}

	/**
	 * Is empty
	 */
	@computed public get isEmpty() {
		return this.data.length === 0;
	}

	/**
	 * Replace
	 * @param values
	 */
	@action.bound public replace(values: I[], cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		this.data.replace(values);
		cb && cb();
	}

	/**
	 * Remove item
	 * @param value
	 * @param cb
	 */
	@action.bound public remove(value: I, cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		this.data.remove(value);
		cb && cb();
	}

	/**
	 * Push
	 * @param value
	 * @param cb
	 */
	@action.bound public push(value: I | I[], cb?: (length: number) => void) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		if (Array.isArray(value)) {
			const r = this.data.push(...value);
			cb && cb(this.data.length);
			return this.data.length;
		} else {
			const r = this.data.push(value);
			cb && cb(this.data.length);
			return r;
		}
	}

	/**
	 * Pop
	 * @param cb
	 * @returns
	 */
	@action.bound public pop(cb?: (item: I) => void) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		const r = this.data.pop();
		cb && cb(r);
		return r;
	}

	/**
	 * Shift
	 * @param cb
	 * @returns
	 */
	@action.bound public shift(cb?: (item: I) => void) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		const r = this.data.shift();
		cb && cb(r);
		return r;
	}

	/**
	 * Unshift
	 * @param item
	 * @param cb
	 * @returns
	 */
	@action.bound public unshift(item: I, cb?: (item: I) => void) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		const r = this.data.unshift(item);
		cb && cb(item);
		return r;
	}

	/**
	 * Splice
	 * @param index
	 * @param delCount
	 * @param items
	 * @param cb
	 * @returns
	 */
	@action.bound public splice(index: number, delCount: number, items?: I[], cb?: (removed?: I[]) => void) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		const r = this.data.splice(index, delCount, ...(items ?? []));
		cb && cb(r);
		return r;
	}

	/**
	 * Swap items
	 * @param key
	 * @param sIndex
	 * @param dIndex
	 * @param cb
	 * @returns
	 */
	@action.bound public swap(sIndex: number, dIndex: number, cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		const b = this.data[sIndex];
		this.data[sIndex] = this.data[dIndex];
		this.data[dIndex] = b;

		cb && cb();
	}

	/**
	 * Reposition element at index
	 * @param sIndex
	 * @param dIndex
	 * @param cb
	 */
	public removeAndInsert(sIndex: number, dIndex: number, cb?: Callback) {
		if (sIndex !== dIndex && sIndex >= 0 && sIndex < this.data.length && dIndex >= 0 && dIndex < this.data.length) {
			this.onBeforeChange && this.onBeforeChange(this.data);

			const el = this.data[sIndex];

			this.splice(sIndex, 1, [], () => {
				this.splice(dIndex, 0, [el], () => {
					cb && cb();
				});
			});
		}
	}

	/**
	 * Remove all items
	 */
	@action.bound public clear(cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		const res = this.data.clear();
		cb && cb();

		return res;
	}

	/**
	 * Load defaults
	 * @param cb
	 */
	@action public loadDefaults(cb?: Callback) {
		this.onBeforeChange && this.onBeforeChange(this.data);

		this.data.replace(this.defaults);
		cb && cb();
	}

	/**
	 * To JS
	 * @returns
	 */
	public toJS(): I[] {
		return toJS(this.data);
	}
}
